import { createContext, useContext, useEffect, useMemo, useReducer, useState, useCallback } from "react"

import useDebounce from "hooks/useDebounce"

import useGetAdminKeywords from "api/querries/content/useGetAdminKeywords"
import useGetClientKeywords from "api/querries/content/useGetClientKeywords"
import { IContentKeyword, IContent, IGetContentKeywordsQueryParams, IGetContentTypesQueryParams } from "types/content"
import { LANGS } from "types/shared"

import { INITIAL_LANGUAGE_SETTINGS_CONTEXT, INITIAL_LANGUAGE_SETTINGS_STATE, KEYWORD_TYPES } from "./LanguageSettingsProviderConstants"
import { IContentTypeInputValues, IInputValueItem, ILanguageSettingsContext, ILanguageSettingsProviderProps } from "./LanguageSettingsProviderTypes"
import useGetContentTypes from "api/querries/content/useGetContentTypes"
import useUpdateAdminKeywords from "api/mutations/content/useUpadteAdminKeywords"
import useUpdateClientKeywords from "api/mutations/content/useUpdateClientKeywords"
import { formatKeywordsData, formatContentTypes } from "./utils"
import { useUI } from "providers/UIProvider"
import { UI_INITIAL_STATE } from "providers/UIProvider/UIProviderConstants"
import useUpdateTypeContent from "api/mutations/content/useUpdateTypeContent"

export const LanguageSettingsContext = createContext<ILanguageSettingsContext>(INITIAL_LANGUAGE_SETTINGS_CONTEXT)

export const useLanguageSettings: () => ILanguageSettingsContext = () => useContext(LanguageSettingsContext)

const LanguageSettingsProvider: React.FC<ILanguageSettingsProviderProps> = ({ children }) => {
  const { T, toggleModal } = useUI()

  const [state, setState] = useReducer(
    (oldState: any, newState: any) => ({ ...oldState, ...newState }),
    INITIAL_LANGUAGE_SETTINGS_STATE,
  )

  const debouncedValue = useDebounce(state.searchValue, 1000)

  const [keywordsQuery, setKeywordsQuery] = useState<IGetContentKeywordsQueryParams>({
    pageSize: 10,
    pageIndex: 1,
    sortBy: "id"
  })

  const [contentTypesQuery, setContentTypesQuery] = useState<IGetContentTypesQueryParams>({
    search: ""
  })

  const { data: clientKeywordsData, isLoading: clientKeywordsLoading, isFetched: clientKeywordsFetched, refetch: refetchClientKeywords } = useGetClientKeywords({
    lang: state.currentLanguage,
    query: keywordsQuery,
    enabled: state.currentKeywordType === KEYWORD_TYPES.MOBILE
  })

  const { data: adminKeywordsData, isLoading: adminKeywordsLoading, isFetched: adminKeywordsFetched, refetch: refetchAdminKeywords } = useGetAdminKeywords({
    lang: state.currentLanguage,
    query: keywordsQuery,
    enabled: state.currentKeywordType === KEYWORD_TYPES.ADMIN
  })

  const { data: contentTypesData, isFetched: contentTypesFetched, refetch: refetchContentTypes } = useGetContentTypes({
    query: contentTypesQuery,
    enabled: state.currentKeywordType === KEYWORD_TYPES.TYPES && state.contentTypes.length === 0
  })

  const { mutate: updateClientKeywords, isLoading: updateClientKeywordsLoading, isSuccess: updateClientKeywordsSuccess } = useUpdateClientKeywords()

  const { mutate: updateAdminKeywords, isLoading: updateAdminKeywordsLoading, isSuccess: updateAdminKeywordsSuccess } = useUpdateAdminKeywords()

  const { mutate: updateTypeContent, isLoading: updateContentTypes, isSuccess: updateContentTypesSuccess } = useUpdateTypeContent()

  const inputValuesChanged = () => {
    const key = state.currentKeywordType.toLowerCase()
    const initialValues = state.initialInputValues[key]
    const updatedValues = state.inputValues[key]

    return JSON.stringify(initialValues) !== JSON.stringify(updatedValues)
  }

  const contentTypeInputValuesChanged = () => {
    const initialValues = state.initialContentTypeInputValues
    const updatedValues = state.contentTypeInputValues

    return JSON.stringify(initialValues) !== JSON.stringify(updatedValues)
  }

  const handleKeywordTabChange = (newTab: KEYWORD_TYPES) => {
    const hasDiff = inputValuesChanged()

    if (hasDiff) {
      toggleModal("confirmationModal", true, {
        title: T("language_settings.changes_would_be_lost_warning"),
        handleSubmit: () => {
          toggleModal("confirmationModal", false, UI_INITIAL_STATE.modals.confirmationModal.data)
          setState({ currentKeywordType: newTab })
          setKeywordsQuery({
            ...keywordsQuery,
            pageIndex: 1
          })
        }
      })
    } else {
      setState({ currentKeywordType: newTab })
      setKeywordsQuery({
        ...keywordsQuery,
        pageIndex: 1
      })
    }
  }

  const handleLanguageTabChange = (newTab: LANGS) => {
    const hasDiff = inputValuesChanged()
    if (hasDiff) {
      toggleModal("confirmationModal", true, {
        title: T("language_settings.changes_would_be_lost_warning"),
        handleSubmit: () => {
          toggleModal("confirmationModal", false, UI_INITIAL_STATE.modals.confirmationModal.data)
          setState({ currentLanguage: newTab })
        }
      })
    } else {
      setState({ currentLanguage: newTab })

    }

  }
  const handleKeywordsPaginationClick = (newPage: number) => {
    const hasDiff = inputValuesChanged()

    if (hasDiff) {
      toggleModal("confirmationModal", true, {
        title: T("language_settings.changes_would_be_lost_warning"),
        handleSubmit: () => {
          toggleModal("confirmationModal", false, UI_INITIAL_STATE.modals.confirmationModal.data)
          setKeywordsQuery({
            ...keywordsQuery,
            pageIndex: newPage
          })
        }
      })
    } else {
      setKeywordsQuery({
        ...keywordsQuery,
        pageIndex: newPage
      })
    }
  }

  const handleSearchChange = useCallback((e: any) => setState({ searchValue: e.target.value }), [])

  const handleKeywordInputChange = (e: any) => {
    const { name, value } = e.target
    const currentTab = state.currentKeywordType.toLowerCase()

    setState({
      inputValues: {
        ...state.inputValues,
        [currentTab]: {
          ...state.inputValues[currentTab],
          [name]: value
        }
      }
    })
  }

  const handleContentTypeInputChange = (e: any, lang: string) => {
    const { name, value } = e.target

    setState({
      contentTypeInputValues: {
        ...state.contentTypeInputValues,
        [name]: {
          ...state.contentTypeInputValues[name],
          [lang]: value
        }
      }
    })
  }

  const handleSubmit = () => {
    if (state.currentKeywordType === KEYWORD_TYPES.MOBILE) {
      const hasDiff = inputValuesChanged()
      if (hasDiff) {
        updateClientKeywords({ lang: state.currentLanguage, data: formatKeywordsData(state.inputValues.mobile) })
      }
    }

    if (state.currentKeywordType === KEYWORD_TYPES.ADMIN) {
      const hasDiff = inputValuesChanged()
      if (hasDiff) {
        updateAdminKeywords({ lang: state.currentLanguage, data: formatKeywordsData(state.inputValues.admin) })
      }
    }

    if (state.currentKeywordType === KEYWORD_TYPES.TYPES) {
      const hasDiff = contentTypeInputValuesChanged()
      if (hasDiff) {
        updateTypeContent({ data: formatContentTypes(state.contentTypeInputValues) })
      }
    }
  }

  const generateInputValuesFromKeywords = (keywords: IContentKeyword[]) => {
    const inputVals: IInputValueItem = {}

    keywords.forEach((keyword) => {
      inputVals[keyword.key] = keyword.value
    })

    return inputVals
  }

  const generateInputValuesFromContentTypes = (contentTypes: IContent[]) => {
    const inputVals: IContentTypeInputValues = {}

    contentTypes.forEach((contentType) => {
      inputVals[`${contentType.objectType}-${contentType.entityType}-${contentType.key}`] = {
        en: contentType.langs.en,
        ka: contentType.langs.ka
      }
    })

    return inputVals
  }

  useEffect(() => {
    if (clientKeywordsData && clientKeywordsFetched) {
      setState({
        keywords: clientKeywordsData.data,
        initialInputValues: {
          ...state.initialInputValues,
          mobile: generateInputValuesFromKeywords(clientKeywordsData.data.data)
        },
        inputValues: {
          ...state.inputValues,
          mobile: generateInputValuesFromKeywords(clientKeywordsData.data.data)
        }
      })
    }
  }, [clientKeywordsData])

  useEffect(() => {
    if (adminKeywordsData && adminKeywordsFetched) {
      setState({
        keywords: adminKeywordsData.data,
        initialInputValues: {
          ...state.initialInputValues,
          admin: generateInputValuesFromKeywords(adminKeywordsData.data.data)
        },
        inputValues: {
          ...state.inputValues,
          admin: generateInputValuesFromKeywords(adminKeywordsData.data.data)
        }
      })
    }
  }, [adminKeywordsData])

  useEffect(() => {
    if (contentTypesData && contentTypesFetched) {
      setState({
        contentTypes: contentTypesData.data,
        initialContentTypeInputValues: {
          ...state.initialContentTypeInputValues,
          ...generateInputValuesFromContentTypes(contentTypesData.data)
        },
        contentTypeInputValues: {
          ...state.contentTypeInputValues,
          ...generateInputValuesFromContentTypes(contentTypesData.data)
        }
      })
    }
  }, [contentTypesData])

  useEffect(() => {
    if (updateClientKeywordsSuccess) {
      refetchClientKeywords()
    }
  }, [updateClientKeywordsSuccess])

  useEffect(() => {
    if (updateAdminKeywordsSuccess) {
      refetchAdminKeywords()
    }
  }, [updateAdminKeywordsSuccess])

  useEffect(() => {
    if (updateContentTypesSuccess) {
      refetchContentTypes()
    }
  }, [updateContentTypesSuccess])

  useEffect(() => {
    if (debouncedValue !== undefined && (clientKeywordsFetched || adminKeywordsFetched) && (state.currentKeywordType === KEYWORD_TYPES.MOBILE || state.currentKeywordType === KEYWORD_TYPES.ADMIN)) {
      setKeywordsQuery({
        ...keywordsQuery,
        pageIndex: 1,
        search: debouncedValue
      })
    }

    if (debouncedValue !== undefined && contentTypesFetched && state.currentKeywordType === KEYWORD_TYPES.TYPES) {
      const contentTypes = contentTypesData.data

      const updatedContentTypes = contentTypes.filter((contentType: IContent) => {
        return (contentType.objectType.toLowerCase().includes(debouncedValue.toLowerCase()) || contentType.entityType.toLowerCase().includes(debouncedValue.toLowerCase()))
      })

      setState({
        contentTypes: updatedContentTypes
      })
    }
  }, [debouncedValue])

  useEffect(() => {
    if (state.currentKeywordType === KEYWORD_TYPES.TYPES) {
      setState({ searchValue: "" })
    }
  }, [state.currentKeywordType])

  const value = useMemo(() => ({
    ...state,
    keywordsQuery,
    handleKeywordTabChange,
    handleLanguageTabChange,
    handleSearchChange,
    handleKeywordInputChange,
    handleContentTypeInputChange,
    handleKeywordsPaginationClick,
    handleSubmit
  }), [state, keywordsQuery])

  return (
    <LanguageSettingsContext.Provider value={value}>
      {children}
    </LanguageSettingsContext.Provider>
  )
}

export default LanguageSettingsProvider